// ============================================================================
// 💬 MESSAGEBUBBLE — Burbuja de Mensaje Individual (V3 — Swipe WhatsApp-Grade)
// src/features/mensagens/components/chat/MessageBubble.jsx
// ============================================================================
// 🔥 V3: Swipe izquierda → revela botón eliminar (como WhatsApp)
// 🔥 V3: Swipe derecha → revela botón responder (como WhatsApp)
// 🔥 V3: Long press → menú de acciones (mobile)
// 🔥 V3: Click derecho → menú contextual (desktop)
// 🔥 V3: Animación de salida al eliminar (exit animation)
// 🔥 V3: Soporte para attachments (imagen/documento)
// ============================================================================

import React, { useState, useCallback, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  Copy,
  Forward,
  Trash2,
  Edit3,
  Reply,
  Image,
  FileText,
  Download,
  ExternalLink,
  X,
} from 'lucide-react';
import { useIsMobile } from '../../../../hooks/useMediaQuery';
import {
  formatConversationTime,
  formatMessageStatus,
} from '../../utils/formatters';
import {
  MESSAGE_STATUS,
  MESSAGE_DIRECTION,
  MESSAGE_STATUS_COLORS,
  MESSAGE_CONTENT_TYPE,
} from '../../utils/constants';

// 🔥 V3: Configuración de swipe
const SWIPE_THRESHOLD = 60; // px para activar acción
const SWIPE_MAX = 100;      // px máximo de desplazamiento

const MessageBubble = memo(({
  message,
  isLast = false,
  onCopy,
  onForward,
  onDelete,
  onEdit,
  onReply,
  showStatus = true,
}) => {
  const isMobile = useIsMobile();
  
  // --------------------------------------------------------------------------
  // 📦 ESTADO
  // --------------------------------------------------------------------------
  const [showActions, setShowActions] = useState(false);
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });

  // --------------------------------------------------------------------------
  // 🔧 REFERENCIAS
  // --------------------------------------------------------------------------
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const isSwipingRef = useRef(false);
  const bubbleRef = useRef(null);

  // --------------------------------------------------------------------------
  // 📊 DATOS DEL MENSAJE
  // --------------------------------------------------------------------------
  const isOutbound = message.direction === MESSAGE_DIRECTION.OUTBOUND;
  const isFailed = message.status === MESSAGE_STATUS.FAILED;
  const isRead = message.status === MESSAGE_STATUS.READ;
  const isDelivered = message.status === MESSAGE_STATUS.DELIVERED;
  const isSending = message.status === MESSAGE_STATUS.SENDING;

  const body = message.content?.body || message.message || '';
  const timestamp = message.timestamp || message.createdAt || 0;
  const edited = message.edited || false;
  const attachments = message.attachments || [];

  // --------------------------------------------------------------------------
  // 🎨 ICONOS DE ESTADO
  // --------------------------------------------------------------------------
  const StatusIcon = isFailed
    ? AlertCircle
    : isRead
      ? CheckCheck
      : isDelivered
        ? CheckCheck
        : isSending
          ? Clock
          : Check;

  const statusIconColor = isRead
    ? 'text-blue-500'
    : isFailed
      ? 'text-red-500'
      : isDelivered
        ? 'text-gray-500'
        : 'text-gray-400';

  // --------------------------------------------------------------------------
  // 👆 SWIPE HANDLERS (Mobile - como WhatsApp)
  // --------------------------------------------------------------------------

  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    isSwipingRef.current = false;
    setSwiping(true);
  }, [isMobile]);

  const handleTouchMove = useCallback((e) => {
    if (!isMobile || !swiping) return;
    
    const deltaX = e.touches[0].clientX - touchStartX.current;
    const deltaY = e.touches[0].clientY - touchStartY.current;
    
    // Detectar si es swipe horizontal (no vertical)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      isSwipingRef.current = true;
      
      // Limitar el desplazamiento máximo
      const clampedX = Math.max(-SWIPE_MAX, Math.min(SWIPE_MAX, deltaX));
      setSwipeX(clampedX);
    }
  }, [isMobile, swiping]);

  const handleTouchEnd = useCallback(() => {
    if (!isMobile) return;
    setSwiping(false);
    
    const duration = Date.now() - touchStartTime.current;
    const wasQuickSwipe = duration < 300;
    
    // Swipe izquierda → eliminar
    if (swipeX < -SWIPE_THRESHOLD || (swipeX < -30 && wasQuickSwipe)) {
      setConfirmDelete(true);
      setSwipeX(-SWIPE_MAX);
    }
    // Swipe derecha → responder
    else if (swipeX > SWIPE_THRESHOLD || (swipeX > 30 && wasQuickSwipe)) {
      if (onReply) {
        onReply(message);
      }
      setSwipeX(0);
    }
    // No llegó al umbral → reset
    else {
      setSwipeX(0);
    }

    // Si fue un tap (no swipe) → mostrar acciones
    if (!isSwipingRef.current && duration < 300) {
      setShowActions(prev => !prev);
    }
  }, [isMobile, swipeX, onReply, message]);

  // --------------------------------------------------------------------------
  // 🖱️ CONTEXT MENU (Desktop - click derecho)
  // --------------------------------------------------------------------------

  const handleContextMenu = useCallback((e) => {
    if (isMobile) return;
    e.preventDefault();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  }, [isMobile]);

  const handleCloseContextMenu = useCallback(() => {
    setShowContextMenu(false);
  }, []);

  // --------------------------------------------------------------------------
  // 🎯 ACCIONES
  // --------------------------------------------------------------------------

  const handleAction = useCallback((action, arg) => {
    action?.(arg);
    setShowActions(false);
    setShowContextMenu(false);
    setSwipeX(0);
    setConfirmDelete(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (onDelete) {
      onDelete(message.id);
    }
    setConfirmDelete(false);
    setSwipeX(0);
  }, [onDelete, message.id]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDelete(false);
    setSwipeX(0);
  }, []);

  // --------------------------------------------------------------------------
  // 📎 RENDER ATTACHMENTS
  // --------------------------------------------------------------------------

  const renderAttachments = () => {
    if (!attachments.length) return null;
    
    return (
      <div className="mt-2 space-y-1">
        {attachments.map((att, idx) => (
          <a
            key={idx}
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
              isOutbound
                ? 'bg-blue-400/30 text-white hover:bg-blue-400/50'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            } transition-colors`}
            onClick={(e) => e.stopPropagation()}
          >
            {att.type === 'image' ? (
              <Image size={14} className="flex-shrink-0" />
            ) : (
              <FileText size={14} className="flex-shrink-0" />
            )}
            <span className="truncate flex-1">{att.name || 'Arquivo'}</span>
            {att.size && (
              <span className="text-[10px] opacity-70 flex-shrink-0">
                {Math.round(att.size / 1024)}KB
              </span>
            )}
          </a>
        ))}
      </div>
    );
  };

  // --------------------------------------------------------------------------
  // 🎨 RENDER
  // --------------------------------------------------------------------------

  return (
    <>
      <motion.div
        ref={bubbleRef}
        initial={{ opacity: 0, y: 8, scale: 0.98 }}
        animate={{ 
          opacity: 1, 
          y: 0, 
          scale: 1,
          x: swiping ? swipeX : 0 
        }}
        exit={{ 
          opacity: 0, 
          x: isOutbound ? 50 : -50,
          scale: 0.9,
          transition: { duration: 0.2, ease: 'easeInOut' }
        }}
        transition={{ 
          type: 'spring', 
          stiffness: 400, 
          damping: 25,
          x: { type: 'spring', stiffness: 300, damping: 30 }
        }}
        className={`
          flex mb-1 px-3 relative
          ${isOutbound ? 'justify-end' : 'justify-start'}
          ${swiping ? 'cursor-grabbing' : ''}
          select-none
        `}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onContextMenu={handleContextMenu}
      >
        {/* 🔥 V3: Indicador de acción swipe izquierda (eliminar) */}
        {isMobile && swipeX < -10 && !confirmDelete && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full p-2 shadow-lg z-0">
            <Trash2 size={16} />
          </div>
        )}

        {/* 🔥 V3: Indicador de acción swipe derecha (responder) */}
        {isMobile && swipeX > 10 && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 bg-blue-500 text-white rounded-full p-2 shadow-lg z-0">
            <Reply size={16} />
          </div>
        )}

        {/* 🔥 V3: Confirmación de eliminación */}
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 bg-card rounded-xl shadow-xl p-2 z-20 border border-gray-200"
          >
            <span className="text-xs text-gray-600 whitespace-nowrap">Excluir?</span>
            <button
              onClick={handleConfirmDelete}
              className="px-2 py-1 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600"
            >
              Sim
            </button>
            <button
              onClick={handleCancelDelete}
              className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-lg hover:bg-gray-300"
            >
              Não
            </button>
          </motion.div>
        )}

        {/* 🔥 V3: Acciones rápidas (mobile: long press, desktop: siempre visibles en hover) */}
        <AnimatePresence>
          {showActions && !confirmDelete && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, x: isOutbound ? 10 : -10 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className={`
                flex items-center gap-0.5 z-10
                ${isOutbound ? 'order-1 mr-1' : 'order-0 mr-1'}
              `}
            >
              {onReply && (
                <button
                  onClick={() => handleAction(onReply, message)}
                  className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-500 transition-colors"
                  aria-label="Responder"
                >
                  <Reply size={14} />
                </button>
              )}
              {onCopy && (
                <button
                  onClick={() => handleAction(onCopy, message)}
                  className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors"
                  aria-label="Copiar"
                >
                  <Copy size={14} />
                </button>
              )}
              {onForward && isOutbound && (
                <button
                  onClick={() => handleAction(onForward, message)}
                  className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors"
                  aria-label="Reenviar"
                >
                  <Forward size={14} />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-500 transition-colors"
                  aria-label="Excluir"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ================================================================ */}
        {/* Burbuja del mensaje */}
        {/* ================================================================ */}
        <div
          className={`
            relative max-w-[80%] md:max-w-[70%] px-3.5 py-2 rounded-2xl
            ${isOutbound
              ? 'bg-[#d9fdd3] text-gray-900 rounded-br-md'
              : 'bg-card text-gray-900 rounded-bl-md'
            }
            ${isFailed ? 'opacity-80 border border-red-300' : ''}
            ${isSending ? 'opacity-70' : ''}
            shadow-sm
            cursor-pointer
            transition-shadow hover:shadow-md
          `}
          onClick={() => {
            if (isMobile) {
              setShowActions(prev => !prev);
            }
          }}
        >
          {/* 📎 Attachments (si existen) */}
          {renderAttachments()}

          {/* 💬 Texto */}
          {body && (
            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words select-text">
              {body}
            </p>
          )}

          {/* Sin texto ni attachments */}
          {!body && !attachments.length && (
            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words select-text italic text-gray-400">
              Mensagem vazia
            </p>
          )}

          {/* Footer: Hora + Estado + Editado */}
          <div className={`
            flex items-center gap-1 mt-1
            ${isOutbound ? 'justify-end' : 'justify-start'}
          `}>
            {/* Indicador de editado */}
            {edited && (
              <span className={`text-[10px] ${isOutbound ? 'text-gray-400' : 'text-gray-400'}`}>
                editada
              </span>
            )}

            {/* Hora */}
            <span className={`text-[10px] select-none ${
              isOutbound ? 'text-gray-400' : 'text-gray-400'
            }`}>
              {formatConversationTime(timestamp)}
            </span>

            {/* Estado (solo mensajes salientes) */}
            {isOutbound && showStatus && (
              <StatusIcon
                size={12}
                className={`flex-shrink-0 ${statusIconColor}`}
              />
            )}
          </div>

          {/* Indicador de fallo */}
          {isFailed && (
            <div className="flex items-center gap-1 mt-1 text-red-600 text-[10px]">
              <AlertCircle size={10} />
              <span>Falha no envio</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* 🔥 V3: Menú contextual (Desktop) */}
      <AnimatePresence>
        {showContextMenu && (
          <>
            {/* Overlay para cerrar */}
            <div
              className="fixed inset-0 z-40"
              onClick={handleCloseContextMenu}
            />
            
            {/* Menú */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.15 }}
              className="fixed z-50 bg-card rounded-xl shadow-xl border border-gray-200 py-1 min-w-[160px]"
              style={{
                left: `${contextMenuPos.x}px`,
                top: `${contextMenuPos.y}px`,
                transform: 'translate(-50%, -100%)',
              }}
            >
              {onReply && (
                <button
                  onClick={() => handleAction(onReply, message)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Reply size={14} />
                  Responder
                </button>
              )}
              {onCopy && (
                <button
                  onClick={() => handleAction(onCopy, message)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Copy size={14} />
                  Copiar
                </button>
              )}
              {onForward && (
                <button
                  onClick={() => handleAction(onForward, message)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Forward size={14} />
                  Reenviar
                </button>
              )}
              {onDelete && (
                <>
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={() => handleAction(() => setConfirmDelete(true))}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={14} />
                    Excluir
                  </button>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
});

MessageBubble.displayName = 'MessageBubble';
export { MessageBubble };
export default MessageBubble;